<template>
    <div>
        <!-- Province select — filter only, not saved -->
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("sangkatCommune.cityProvince")
                }}</label>
                <Select
                    v-model="selected_province"
                    :placeholder="$t('sangkatCommune.cityProvince')"
                    filterable
                    @on-change="onProvinceChange"
                >
                    <Option
                        v-for="province in provinces"
                        :key="province.province_code"
                        :value="province.province_code"
                    >
                        {{ province.name_en }}
                    </Option>
                </Select>
            </div>
        </div>

        <!-- District select — filtered by province -->
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("sangkatCommune.khanDistrict")
                }}</label>
                <Select
                    v-model="model.district_code"
                    :placeholder="$t('sangkatCommune.khanDistrict')"
                    :class="{ 'is-invalid': errors.has('district_code') }"
                    :disabled="!selected_province && !isUpdate"
                    filterable
                >
                    <Option
                        v-for="district in districts"
                        :key="district.district_code"
                        :value="district.district_code"
                    >
                        {{ district.name_en }}
                    </Option>
                </Select>
                <div
                    class="invalid-feedback"
                    v-if="errors.has('district_code')"
                >
                    {{ errors.first("district_code") }}
                </div>
            </div>
        </div>

        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("sangkatCommune.nameKh")
                }}</label>
                <input
                    v-model.trim="model.name_kh"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('name_kh') }"
                    ref="name_kh"
                />
                <div class="invalid-feedback" v-if="errors.has('name_kh')">
                    {{ errors.first("name_kh") }}
                </div>
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("sangkatCommune.nameEn")
                }}</label>
                <input
                    v-model.trim="model.name_en"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('name_en') }"
                />
                <div class="invalid-feedback" v-if="errors.has('name_en')">
                    {{ errors.first("name_en") }}
                </div>
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("sangkatCommune.chiefName")
                }}</label>
                <input
                    v-model.trim="model.commune_chief_name"
                    type="text"
                    class="form-control"
                />
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("sangkatCommune.chiefPhone")
                }}</label>
                <input
                    v-model.trim="model.commune_phone_number"
                    type="text"
                    class="form-control"
                />
            </div>
        </div>
        <div slot="footer">
            <div class="tw-flex tw-justify-end tw-space-x-2 tw-sh">
                <ts-button
                    @click.prevent="() => $emit('cancel')"
                    class="btn-gray"
                >
                    {{ $t("cancel") }}
                </ts-button>
                <ts-button
                    v-if="!isUpdate"
                    color="primary"
                    outline
                    :waiting="waiting_new"
                    :disabled="waiting_new || waiting"
                    @click.prevent="onSaveAddNew"
                    >{{ $t("saveAddNew") }}</ts-button
                >
                <ts-button
                    color="primary"
                    v-if="!isUpdate"
                    :waiting="waiting"
                    :disabled="waiting_new || waiting"
                    @click.prevent="onSave"
                    >{{ $t("save") }}</ts-button
                >
                <ts-button
                    v-if="isUpdate"
                    color="primary"
                    @click.prevent="onUpdate"
                    :waiting="waiting"
                    >{{ $t("update") }}</ts-button
                >
            </div>
        </div>
        <Spin size="large" fix v-if="loading"> </Spin>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapState } from "vuex";
import { isEmpty } from "lodash";

export default {
    name: "sangkat-communeForm",
    data() {
        return {
            errors: new Errors(),
            loading: false,
            waiting: false,
            waiting_new: false,
            districts: [],
            provinces: [],
            selected_province: null, // ← not saved, filter only
            model: {
                district_code: null,
                name_kh: null,
                name_en: null,
                commune_chief_name: null,
                commune_phone_number: null
            }
        };
    },
    computed: {
        ...mapState("corporate/sangkatCommune", ["edit_data"]),
        isUpdate() {
            return !isEmpty(this.edit_data);
        }
    },
    methods: {
        fetchProvinces() {
            this.$store
                .dispatch("corporate/sangkatCommune/getCityProvince")
                .then(response => {
                    this.provinces = response.data;
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                });
        },
        fetchDistricts(province_code = null) {
            this.$store
                .dispatch(
                    "corporate/sangkatCommune/getKhanDistrict",
                    province_code
                )
                .then(response => {
                    this.districts = response.data;
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                });
        },
        onProvinceChange(province_code) {
            this.model.district_code = null; // ← reset district when province changes
            this.fetchDistricts(province_code);
        },
        async onSaveAddNew() {
            this.errors = new Errors();
            this.waiting_new = true;
            this.$store
                .dispatch("corporate/sangkatCommune/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("fetchData");
                    this.clearInput();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting_new = false;
                });
        },
        async onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("corporate/sangkatCommune/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        onUpdate() {
            this.waiting = true;
            this.$store
                .dispatch("corporate/sangkatCommune/update", {
                    id: this.edit_data.commune_code,
                    data: this.model
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        setEditData() {
            this.fetchProvinces();
            this.$nextTick(function() {
                this.$refs.name_kh.focus();
            });
            if (!isEmpty(this.edit_data)) {
                // ← on edit, derive province from district_code first 2 digits
                const provinceCode = this.edit_data.district_code
                    ? this.edit_data.district_code.substring(0, 2)
                    : null;
                this.selected_province = provinceCode;

                // load districts filtered by province then set district
                if (provinceCode) {
                    this.fetchDistricts(provinceCode);
                }

                this.model.district_code = this.edit_data.district_code;
                this.model.name_kh = this.edit_data.name_kh;
                this.model.name_en = this.edit_data.name_en;
                this.model.commune_chief_name = this.edit_data.commune_chief_name;
                this.model.commune_phone_number = this.edit_data.commune_phone_number;
            }
        },
        clearInput() {
            this.errors = new Errors();
            this.selected_province = null;
            this.districts = [];
            this.model.district_code = null;
            this.model.name_kh = null;
            this.model.name_en = null;
            this.model.commune_chief_name = null;
            this.model.commune_phone_number = null;
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "SANGKAT COMMUNE",
                desc: not.text
            });
        }
    }
};
</script>
